import store from '@/store/store';
import { actions } from '@/models/store.actions';
import AppLoading from '@/components/Main/AppLoading.vue';
import Vue from 'vue';

let loader: IAppLoaderManager | undefined;
let appContext: any;

export interface IAppLoaderOptions {
  container?: HTMLElement;
  cancelCallback?: () => void;
}

export interface IAppLoaderTextOptions {
  text?: string;
  class?: any;
}

export interface IAppLoaderProgressOptions {
  showTotal?: boolean;
  total?: number;
  current: number;
}

function createShowLoading(ctx: any): any {
  return function(
    incOptions: IAppLoaderOptions | undefined,
    isGlobal: boolean = false,
    textOptions: IAppLoaderTextOptions | undefined = undefined,
    progressOptions: IAppLoaderProgressOptions | undefined = undefined): IAppLoaderManager | undefined {
    let __loader: IAppLoaderManager | undefined;

    if (isGlobal) {
      if (!loader && ctx) {
        loader = createAppLoader(ctx, incOptions, textOptions, progressOptions);
      }
    } else {
      __loader = ctx ? createAppLoader(ctx, incOptions, textOptions, progressOptions) : createLoaderManager();
    }

    return __loader;
  };
}

function createAppLoader(
  ctx: any,
  loaderOptions: IAppLoaderOptions | undefined,
  textOptions: IAppLoaderTextOptions | undefined = undefined,
  progressOptions: IAppLoaderProgressOptions | undefined = undefined): IAppLoaderManager {
  const AppLoadingClass = Vue.extend(Object.assign(AppLoading, { parent: ctx }) as any);

  const fullPage = !loaderOptions?.container;

  const instance = new AppLoadingClass({ propsData: { fullPage } });

  instance.$mount();

  const parent = fullPage ? document.getElementsByClassName('v-application')[0] as HTMLElement : loaderOptions!.container!;

  parent.appendChild(instance.$el);

  const manager = createLoaderManager(instance, parent);

  if (textOptions) {
    manager.setText(textOptions);
  }

  if (progressOptions) {
    manager.setProgress(progressOptions);
  }

  if (loaderOptions?.cancelCallback) {
    manager.setCancelCallback(loaderOptions?.cancelCallback);
  }

  return manager;
}

interface IAppLoaderManager {
  hide(): void;

  setProgress(opts: IAppLoaderProgressOptions): void;

  setText(opts: IAppLoaderTextOptions): void;

  hideProgress(): void;

  setCancelCallback(cb: (() => void) | null): void;
}

function createLoaderManager(appLoaderInstance?: any, parentElement?: HTMLElement): IAppLoaderManager {
  return {
    hide() {
      if (appLoaderInstance) {
        appLoaderInstance.$destroy();
      }
      if (parentElement && appLoaderInstance && appLoaderInstance.$el && appLoaderInstance.$el.parentElement === parentElement) {
        parentElement.removeChild(appLoaderInstance.$el);
      }
    },
    setProgress(opts: IAppLoaderProgressOptions) {
      if (appLoaderInstance) {
        appLoaderInstance.setProgress(opts);
      }
    },
    setText(opts: IAppLoaderTextOptions) {
      if (appLoaderInstance) {
        appLoaderInstance.setText(opts);
      }
    },
    hideProgress() {
      if (appLoaderInstance) {
        appLoaderInstance.hideProgress();
      }
    },
    setCancelCallback(cb: () => void | null): void {
      if (appLoaderInstance) {
        appLoaderInstance.setCancelCallback(cb);
      }
    }
  };
}

export function setLoaderContext(context: any) {
  appContext = context;
}

export function showLoading(incOptions?: IAppLoaderOptions, isGlobal: boolean = false, textOptions: IAppLoaderTextOptions | null = null) {
  return createShowLoading(appContext)(incOptions, isGlobal, textOptions);
}

export function hideLoading(): Promise<boolean> {
  if (loader) {
    loader.hide();
    return new Promise((resolve) => {
      setTimeout(() => {
        loader = undefined;
        resolve(true);
      });
    });
  } else {
    return Promise.resolve(false);
  }
}

export function isLoadingVisible(): boolean {
  return !!loader;
}

export function setLoadingText(opts: IAppLoaderTextOptions): void {
  if (loader) {
    loader.setText(opts);
  }
}

export function setLoadingProgress(opts: IAppLoaderProgressOptions): void {
  if (loader) {
    loader.setProgress(opts);
  }
}

export function hideLoadingProgress(): void {
  if (loader) {
    loader.hideProgress();
  }
}

export function setLoadingCancelCallback(cb: (() => void) | null): void {
  if (loader) {
    loader.setCancelCallback(cb);
  }
}

export function disableShowMsg() {
  changeShowMsg(false);
}

export function enableShowMsg() {
  changeShowMsg(true);
}

function changeShowMsg(v: any) {
  store.commit(actions.SETTTEMP_MUT_ISSHOWMSG, v);
}

export default {
  methods: {
    showLoading: function(incOptions?: IAppLoaderOptions,
      isGlobal: boolean = false,
      textOptions: IAppLoaderTextOptions | null = null,
      progressOptions: IAppLoaderProgressOptions | undefined = undefined) {
      return createShowLoading(this)(incOptions, isGlobal, textOptions, progressOptions);
    },
    hideLoading,
    isLoadingVisible,
    disableShowMsg,
    enableShowMsg,
    setLoadingText,
    setLoadingProgress,
    hideLoadingProgress,
    setLoadingCancelCallback
  }
};
